/**
 * TA Cockpit — Pipeline Bottleneck Radar.
 *
 * Where each open role is stuck, as a traffic light per funnel phase, on live data. Replaces the
 * hand-filled mockup of the same name; the thresholds are unchanged, the typing is gone.
 *
 * Roles are ordered by SEVERITY, not by size. The mockup listed them in the order they were typed,
 * which was fine for seven rows someone had just entered by hand; with every open requisition on
 * the page the first screen has to be the roles that need a decision this week. Open hires break
 * the tie, because a phase that is off on a nine-hire posting costs nine times what it costs on one.
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { fetchBottleneckRadar } from '../../../services/taBottleneckApi';
import type { BottleneckRadarData } from '../../../types/taBottleneck';
import {
  evaluate,
  summarise,
  windowLabel,
  type StageStatus,
} from '../../../utils/bottleneckRadar';
import {
  NO_FILTER,
  applyFilter,
  clusterOptions,
  departmentOptions,
  hiresInView,
  isFiltered,
  type RadarFilter,
} from '../../../utils/bottleneckFilters';
import { Card, SectionHead } from '../rolePrioritisation/prioritisationUi';
import { useInterviewAvailability } from '../../../components/interviewAvailability/useInterviewAvailability';
import { RoleRadarCard } from './RoleRadarCard';
import { KpiTile } from './radarUi';
import { actionsFor } from '../../../utils/radarActions';
import { ActionBoard, boardRows } from './ActionBoard';
import { PortfolioReconciliation, RadarFilters } from './RadarChrome';
import { RadarLegend } from './RadarLegend';
import { logAnalyticsEvent } from '../../../../../analytics/nativeAnalytics';

/** Worst first. `unknown` sorts last: it is not a verdict and must not head the page. */
const SEVERITY: Record<StageStatus, number> = { red: 0, yellow: 1, green: 2, unknown: 3 };

export function BottleneckRadarTab() {
  const [data, setData] = useState<BottleneckRadarData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [filter, setFilter] = useState<RadarFilter>(NO_FILTER);
  /**
   * Undefined until the first payload lands, deliberately: the PROXY owns the default window and
   * the list of windows it will accept, so the client never hardcodes 180 and cannot offer a
   * choice the server would reject.
   */
  const [windowDays, setWindowDays] = useState<number | undefined>(undefined);
  const [windowBusy, setWindowBusy] = useState(false);

  /** Changing the window re-queries the warehouse. The page KEEPS its figures while it does —
   *  blanking the tab for a control somebody is adjusting reads as a crash, not as a refresh. */
  function changeWindow(next: number) {
    setWindowBusy(true);
    setWindowDays(next);
  }

  /*
   * ⚠️ THE CANDIDATE FUNNEL LEFT THIS TAB on Sep 29 2026 (Theresa) — it is on Tracking → Delivery
   * now, `useCandidateFunnel`. It had been here since the Open Positions page was deleted, on the
   * argument that the radar says which ROLES are stuck and the funnel says which STAGE the
   * candidates are in. What it actually is, is a live read of how the trimester is being
   * delivered, which is the question one tab over — and it never honoured this tab's cluster
   * filter, so it sat inside a filtered page describing a different population every time somebody
   * used the control above it.
   */

  /** Loading is set by the caller that bumps `reloadKey`, never inside the effect — a synchronous
   *  setState in an effect body is a cascading render, and the lint rule that says so is right. */
  function reload() {
    setLoading(true);
    setReloadKey((k) => k + 1);
  }

  useEffect(() => {
    let cancelled = false;
    fetchBottleneckRadar(windowDays)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setError(null);
        setLoading(false);
        setWindowBusy(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
        setWindowBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey, windowDays]);

  /**
   * Everything on screen is derived from the FILTERED set — the KPI strip and the reconciliation
   * included. A strip reporting the company while the list below shows one cluster would be two
   * populations on one screen with nothing saying so.
   */
  const view = useMemo(() => {
    if (!data) return null;
    const { positions, unmatched, totals } = applyFilter(data, filter);
    const note = windowLabel(data.windows.conversionDays).toLowerCase();
    const rows = positions
      .map((position) => {
        const evaluation = evaluate(position, data.benchmarks, note);
        return {
          position,
          evaluation,
          // Derived here, beside the evaluation it reads, so the board and the card cannot be
          // looking at two different runs of the same arithmetic.
          actions: actionsFor(position, evaluation, data.benchmarks),
          hiresInView: hiresInView(position, filter),
        };
      })
      .sort(
        (a, b) =>
          SEVERITY[a.evaluation.overall] - SEVERITY[b.evaluation.overall] ||
          b.position.openHires - a.position.openHires ||
          a.position.title.localeCompare(b.position.title)
      );
    return {
      rows,
      board: boardRows(rows),
      unmatched,
      totals,
      summary: summarise(positions, data.benchmarks),
      clusters: clusterOptions(data),
      departments: departmentOptions(data),
    };
  }, [data, filter]);

  /**
   * The interview availability light per role (Case and Final), fetched for every measured
   * posting once the radar has loaded. Its own request and its own failure: a calendar outage
   * leaves the chips empty and never touches the phase lights.
   */
  const availabilityRoles = useMemo(
    () =>
      (data?.positions ?? [])
        .filter((p) => p.measured)
        .map((p) => ({ postingId: p.postingId, title: p.title })),
    [data]
  );
  const { data: availability } = useInterviewAvailability('radar', availabilityRoles);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center gap-2 text-sm text-iron">
        <Loader2 className="h-4 w-4 animate-spin" />
        Reading the radar…
      </div>
    );
  }

  /**
   * ⚠️ THE FULL-PAGE PANEL IS FOR HAVING NOTHING, not for an error. A failed window change used to
   * land here — which replaced the radar AND the window selector with an error page, so the one
   * control that caused it was the one control the reader could no longer reach, and the figures
   * they already had went with it. The tab's own rule is that it keeps its figures while a window
   * reloads; a failed reload is exactly when that matters. (CodeRabbit, PR #463.)
   */
  if (!data || !view) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center gap-3 py-12 text-sm text-[#8A0E22]">
          <AlertCircle className="h-5 w-5" />
          <div>Could not load the radar: {error}</div>
          <p className="max-w-md text-center text-iron">
            The radar needs both the warehouse and the hiring plan — it reads how many hires each
            posting is for from the hiring plan, and coverage cannot be judged without it.
          </p>
          <Button variant="secondary" size="sm" onClick={reload}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const { summary, totals } = view;
  const unmatched = view.unmatched.length;
  const filtered = isFiltered(filter);

  return (
    <div className="space-y-6">
      {/*
        A re-read that failed on top of figures we still have. Stated as a banner rather than as a
        page, and it names BOTH spans: the selector below shows what was asked for, the figures
        show what was last read, and a reader is entitled to know the two disagree.
      */}
      {error ? (
        <div className="flex items-start gap-2 rounded-lg bg-[#FDECEF] px-4 py-3 text-[13px] text-[#8A0E22]">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            Could not re-read the radar: {error}. The figures below are the last read that worked,
            over {windowLabel(data.windows.conversionDays).toLowerCase()} — use Re-read to try
            again.
          </div>
        </div>
      ) : null}

      <section>
        <SectionHead
          number="01"
          title="Where roles stand"
          aside={`${totals.postings} ${totals.postings === 1 ? 'pipeline' : 'pipelines'}`}
        />

        <div className="mb-3">
          <RadarFilters
            filter={filter}
            clusters={view.clusters}
            departments={view.departments}
            onChange={(next) => {
              // Categories only — which AXIS was used, never which cluster or department, and a
              // cleared filter says so rather than sending an empty string.
              void logAnalyticsEvent('ta_radar_filter_changed', {
                cluster: next.cluster ? 'set' : 'cleared',
                department: next.department ? 'set' : 'cleared',
              });
              setFilter(next);
            }}
            /**
             * What was ASKED FOR, falling back to what the payload carries. After a failed
             * re-read these differ, and binding to the payload would snap the control back to the
             * old window — at which point re-picking the one that failed sets state to a value it
             * already holds, no effect re-runs, and the control is dead. The banner above says
             * which figures are on screen.
             */
            window={windowDays ?? data.windows.conversionDays}
            windowChoices={data.windows.conversionChoices}
            onWindowChange={(next) => {
              // The window is a measurement decision, so the VALUE is worth keeping, unlike the
              // filters above where only the axis is recorded.
              void logAnalyticsEvent('ta_radar_window_changed', { windowDays: next });
              changeWindow(next);
            }}
            windowBusy={windowBusy}
          />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <KpiTile label="On track" value={summary.counts.green} status="green" />
          <KpiTile label="At risk" value={summary.counts.yellow} status="yellow" />
          <KpiTile label="Off" value={summary.counts.red} status="red" />
          <KpiTile
            label="Not enough data"
            value={summary.counts.unknown}
            status="unknown"
            note="too new, or no posting"
          />
          <KpiTile
            label="Top bottleneck"
            value={summary.topBottleneck?.name ?? 'None'}
            note={
              summary.topBottleneck
                ? `${summary.topBottleneck.roles} of ${summary.problemRoles} roles needing attention`
                : 'no role is off track'
            }
          />
        </div>

      </section>

      {/*
        The board sits ABOVE the role list on purpose. A lead opens this tab to find this week's
        work, and the list below is where they go to check one role — so the answer comes first and
        the evidence second, the same order the role card itself uses.
      */}
      <ActionBoard rows={view.board} />

      <section>
        <SectionHead
          number="03"
          title="Roles, worst first"
          aside={`as of ${new Date(data.generatedAt).toLocaleString('en-GB', {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}`}
        />
        {view.rows.length ? (
          <div className="flex flex-col gap-2.5">
            {view.rows.map(({ position, evaluation, actions, hiresInView: inView }) => (
              <RoleRadarCard
                key={position.postingId}
                position={position}
                evaluation={evaluation}
                actions={actions}
                hiresInView={inView}
                availability={availability[position.postingId] ?? null}
                expanded={!!expanded[position.postingId]}
                onToggle={() =>
                  setExpanded((e) => {
                    const opening = !e[position.postingId];
                    // Opening only: a close is the same interest already counted, and the status
                    // travels because "which lights people actually look into" is the question.
                    if (opening) {
                      void logAnalyticsEvent('ta_radar_role_opened', {
                        status: evaluation.overall,
                        bottleneck: evaluation.bottleneck?.key ?? 'none',
                      });
                    }
                    return { ...e, [position.postingId]: opening };
                  })
                }
              />
            ))}
          </div>
        ) : (
          <Card className="p-6 text-sm text-iron">
            {filtered
              ? 'No pipeline matches this cluster and department. Clear the filter to see the rest.'
              : 'No open requisition in the hiring plan names a Lever posting, so there is nothing to measure yet.'}
          </Card>
        )}
        <div className="mt-3">
          <Button variant="secondary" size="sm" onClick={reload} disabled={loading}>
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            Re-read
          </Button>
        </div>
      </section>

      <RadarLegend benchmarks={data.benchmarks} windows={data.windows} />

      <div>
        <PortfolioReconciliation totals={totals} filtered={filtered} />

        {/*
          The strip above states HOW MANY are invisible; this names WHICH, and stays collapsed —
          stacked warning blocks are what pushed the figures off the first screen on Role
          Prioritisation, and the same list would do it here.
        */}
        {unmatched ? (
          <details className="mt-2 rounded-lg bg-lightBlue px-4 py-3 text-[13px] text-[#0B4F9E]">
            <summary className="cursor-pointer font-medium">
              Which {unmatched === 1 ? 'role has' : 'roles have'} no posting id
            </summary>
            <div className="mt-2 space-y-1.5">
              <p>
                These hiring-plan rows carry no <strong>Lever Job Posting ID</strong>, so there is no
                funnel to read. Filling the field in Airtable is the whole fix:
              </p>
              <ul className="list-disc pl-5">
                {view.unmatched.map((u) => (
                  <li key={u.positionId || u.title}>
                    {u.title}
                    {u.recruiter ? ` — ${u.recruiter}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          </details>
        ) : null}
      </div>
    </div>
  );
}
